import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import { Image } from "primereact/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  getBanners,
  resetFilters,
  setFilter,
  updateBannerStatus,
} from "store/slices/bannersSlice";
import { showDeleteModal } from "store/slices/modalSlice";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import ListViewTypeToggler from "components/common/table/ListViewTypeToggler";
import GridTypeTable from "components/common/GridTypeTable";
import PageHeader from "components/common/PageHeader";
import GridShimmer from "components/common/GridShimmer";

const Banners = () => {
  const { t } = useTranslation();
  const { trash, edit, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("banners");
  const [viewMode, setViewMode] = useState("grid");

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const {
    loading,
    isLoading,
    loadingItemId,
    items,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy },
  } = useSelector((state) => state.Banners);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, []);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      status: isReset ? -1 : status,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getBanners(payload));
  };

  const handleReset = useCallback(() => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  }, []);

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: STATUS_OPTIONS,
      },
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      {
        header: t("Image"),
        key: "imageUrl",
        render: (value) =>
          value ? (
            <Image
              src={value}
              alt="Image"
              className="banner_thumbnail"
              width={50}
              height={50}
              preview
            />
          ) : (
            "-"
          ),
      },
      {
        header: t("Title"),
        key: "title",
        render: (value) => value?.en || "-",
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            <Switch
              checked={item.status === 1}
              handleOnChange={() => {
                handleStatusUpdate({
                  _id: item._id,
                  status: item.status,
                  dispatch,
                  action: updateBannerStatus,
                });
              }}
              disabled={isLoading && loadingItemId === item._id}
            />
            <img
              src={edit}
              width={20}
              height={20}
              alt={t("edit")}
              onClick={() => navigate(`/banners/edit-banner?id=${item?._id}`)}
            />
          </>
        )}
        <Link to={`/banners/banner-details?id=${item._id}`}>
          <img src={view} width={20} height={20} alt="view" />
        </Link>
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt={t("delete")}
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Banner"),
                  message: t("Are you sure you want to delete this Banner?"),
                  itemId: item?._id,
                  actionType: "BANNER",
                }),
              );
            }}
          />
        )}
      </>
    ),
    [canEdit, canDelete, isLoading, loadingItemId],
  );

  // Grid Card Header Renderer
  const renderBannerCardHeader = useCallback(
    (banner, t) => (
      <>
        <h3 className="banner-title">
          {banner.title?.en || banner.title || "—"}
        </h3>
        {banner.imageUrl && (
          <img
            src={banner.imageUrl}
            height={180}
            width="100%"
            alt={`${banner?.title?.en || "Banner"}`}
            style={{ objectFit: "cover", borderRadius: "8px" }}
          />
        )}
      </>
    ),
    [],
  );

  // Grid Card Body Renderer
  const renderBannerCardBody = useCallback(
    (banner, t) => (
      <div className="banner-info">
        <div className="info-item">
          <span className="label">{t("Created Date")}</span>
          <span className="value">{formatUTCDate(banner.createdAt)}</span>
        </div>
        <div className="info-item">
          <span className="label">{t("Status")}</span>
          <StatusBadge status={banner.status} t={t} />
        </div>
      </div>
    ),
    [],
  );

  // Grid Card Footer Renderer
  const renderBannerCardFooter = useCallback(
    (banner, t) => (
      <div className="grid-actions">
        <div className="left-icons">
          <Link to={`/banners/banner-details?id=${banner._id}`}>
            <img src={view} width={20} height={20} alt="view" />
          </Link>
          {canEdit && (
            <img
              src={edit}
              width={20}
              height={20}
              alt={t("edit")}
              onClick={() => navigate(`/banners/edit-banner?id=${banner._id}`)}
            />
          )}
          {canDelete && (
            <img
              src={trash}
              width={20}
              height={20}
              alt={t("delete")}
              onClick={() =>
                dispatch(
                  showDeleteModal({
                    title: t("Delete Banner"),
                    message: t("Are you sure you want to delete this Banner?"),
                    itemId: banner._id,
                    actionType: "BANNER",
                  }),
                )
              }
            />
          )}
        </div>

        {canEdit && (
          <Switch
            checked={banner.status === 1}
            handleOnChange={() =>
              handleStatusUpdate({
                _id: banner._id,
                status: banner.status,
                dispatch,
                action: updateBannerStatus,
              })
            }
            disabled={isLoading && loadingItemId === banner._id}
          />
        )}
      </div>
    ),
    [canEdit, canDelete, isLoading, loadingItemId],
  );

  const viewToggle = (
    <ListViewTypeToggler
      viewMode={viewMode}
      onChange={(value) => handleViewModeChange(value)}
    />
  );

  return (
    <div className="page-content banners_page">
      <PageHeader title={t("Banners")} />
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Name")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add Banner")}
        onBtnClick={() => navigate("/banners/add-banner")}
        rightActions={viewToggle}
      />

      {viewMode === "list"
        ? loading && <TableShimmer rows={10} columns={4} />
        : loading && <GridShimmer rows={10} columns={4} />}
      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No banners found")} />
      )}

      {!loading && items?.length > 0 && (
        <>
          {viewMode === "grid" ? (
            <GridTypeTable
              data={items}
              renderCardHeader={renderBannerCardHeader}
              renderCardFooter={renderBannerCardFooter}
              emptyMessage={t("No banners found")}
              gridColumns={{ lg: 3, md: 4, sm: 6, xs: 12 }}
              t={t}
            />
          ) : (
            <DataTable
              columns={columns}
              data={items}
              renderActions={renderActions}
              t={t}
            />
          )}

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Banners;
